import React, { useCallback, useEffect, useState } from 'react';
import { BarChart2, Calculator, ChevronDown, ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { fetchYieldDates, fetchYieldLookups, fetchYieldSheets, yieldWrite } from '../services/api';
import { listRatios, money, usDate } from './yieldSheetCalc';
import YieldSheetEditor, { YieldFlagNote } from './YieldSheetEditor';
import YieldSheetSummary, { printArea } from './YieldSheetSummary';
import YieldSheetCounting from './YieldSheetCounting';

/**
 * Procurement > HCD > Yield Sheet: the admin portal's yield sheet list on the SQL01 copy, with its
 * editor, summary pop-up and counting mode (Azure exit WS8). Every read works now. Every save goes
 * through admin-write and stays off until ws8_yield_sheet_live is set at cutover.
 */

const PAGE_SIZE = 25;
const selected = (e) => Array.from(e.target.selectedOptions).map((o) => o.value);

export default function YieldSheet() {
  const [lookups, setLookups] = useState(null);
  const [live, setLive] = useState(false);
  const [filter, setFilter] = useState({ crabSupplier: [], status: [], startDate: '', endDate: '' });
  const [applied, setApplied] = useState({});
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [openDate, setOpenDate] = useState(null);
  const [sheets, setSheets] = useState(null);
  const [editor, setEditor] = useState(null);
  const [summary, setSummary] = useState(null);
  const [counting, setCounting] = useState(null);

  useEffect(() => {
    fetchYieldLookups().then(setLookups).catch((e) => setError(e.message));
    yieldWrite('yield_features').then((r) => setLive(r.live === true)).catch(() => setLive(false));
  }, []);

  const load = useCallback(() => {
    setError(null);
    fetchYieldDates({ ...applied, pageSize: PAGE_SIZE, pageNumber: page }).then(setList).catch((e) => setError(e.message));
  }, [applied, page]);
  useEffect(() => { load(); }, [load]);

  const loadSheets = useCallback((date) => {
    setSheets(null);
    fetchYieldSheets({ ...applied, dateReceived: date }).then(setSheets).catch((e) => setError(e.message));
  }, [applied]);

  const toggle = (date) => {
    if (openDate === date) { setOpenDate(null); return; }
    setOpenDate(date);
    loadSheets(date);
  };

  const search = () => { setPage(1); setOpenDate(null); setApplied(filter); };
  const refresh = () => { load(); if (openDate !== null) loadSheets(openDate); };

  const remove = async (row) => {
    if (row.igrId > 0) { setMessage('Yield Sheet linked to IGR. Deletion not allowed.'); return; }
    if (!window.confirm('Do you want to delete?')) return;
    try {
      await yieldWrite('yield_delete', { purchaseOrderId: row.purchaseOrderId });
      refresh();
    } catch (e) { setMessage(e.message); }
  };

  if (counting) return <YieldSheetCounting {...counting} live={live} onClose={() => setCounting(null)} />;

  const pages = list ? Math.max(1, Math.ceil(list.totalRows / PAGE_SIZE)) : 1;
  return (
    <div>
      <div className="page-header">
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Yield Sheet</h2>
      </div>
      <YieldFlagNote live={live} />
      <div className="ys-filters">
        <label className="ys-field">Supplier:
          <select multiple className="form-input" value={filter.crabSupplier} onChange={(e) => setFilter({ ...filter, crabSupplier: selected(e) })}>
            {(lookups ? lookups.suppliers : []).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="ys-field">Status:
          <select multiple className="form-input" value={filter.status} onChange={(e) => setFilter({ ...filter, status: selected(e) })}>
            {(lookups ? lookups.statuses : []).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="ys-field">Start Date:
          <input type="date" className="form-input" value={filter.startDate} max={filter.endDate || undefined} onChange={(e) => setFilter({ ...filter, startDate: e.target.value })} />
        </label>
        <label className="ys-field">End Date:
          <input type="date" className="form-input" value={filter.endDate} min={filter.startDate || undefined} onChange={(e) => setFilter({ ...filter, endDate: e.target.value })} />
        </label>
        <div className="ys-actions">
          <button type="button" className="btn btn-navy" onClick={search}>Search</button>
          <button type="button" className="btn btn-primary" onClick={() => setEditor({ id: 0 })} disabled={!lookups}>Add Yield Sheet</button>
          <button type="button" className="btn btn-outline" onClick={() => printArea('ys-print-report')}>Print Report</button>
        </div>
      </div>
      {message && <div className="ys-note ys-note-bad">{message}</div>}
      <div className="card" id="ys-print-report">
        <div className="card-header">Dates{list ? ` (${list.totalRows})` : ''}</div>
        {error && <div className="ys-status" style={{ color: 'var(--red)' }}>Error: {error}</div>}
        {!list && !error && <div className="ys-status">Loading...</div>}
        {list && list.purchaseOrders.length === 0 && <div className="ys-status">No yield sheets found.</div>}
        {list && list.purchaseOrders.length > 0 && (
          <div className="port-scroll">
            <table className="trans-table">
              <thead><tr><th>Date Received</th><th>Total Yield Sheet(s)</th><th>Finalized Yield Sheet(s)</th><th aria-label="Open" /></tr></thead>
              <tbody>
                {list.purchaseOrders.map((d) => (
                  <React.Fragment key={d.dateReceived || 'none'}>
                    <tr className="ys-click" onClick={() => toggle(d.dateReceived)}>
                      <td>{usDate(d.dateReceived)}</td><td>{d.totalCount}</td><td>{d.finalizeCount}</td>
                      <td>{openDate === d.dateReceived ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</td>
                    </tr>
                    {openDate === d.dateReceived && (
                      <tr>
                        <td colSpan={4} className="ys-nested">
                          {!sheets && <div className="ys-status">Loading...</div>}
                          {sheets && <SheetTable sheets={sheets} live={live} onEdit={(id) => setEditor({ id })} onDelete={remove}
                            onSummary={(r) => setSummary({ id: r.purchaseOrderId, name: r.crabberName })}
                            onCount={(r) => setCounting({ id: r.purchaseOrderId, name: r.crabberName, date: r.dateReceived })} />}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list && pages > 1 && (
          <div className="ys-pager hide-in-print">
            <button type="button" className="btn btn-outline" disabled={page <= 1} onClick={() => { setOpenDate(null); setPage(page - 1); }}>Previous</button>
            <span>Page {page} of {pages}</span>
            <button type="button" className="btn btn-outline" disabled={page >= pages} onClick={() => { setOpenDate(null); setPage(page + 1); }}>Next</button>
          </div>
        )}
      </div>
      {editor && lookups && <YieldSheetEditor id={editor.id} lookups={lookups} live={live} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); refresh(); }} />}
      {summary && lookups && <YieldSheetSummary id={summary.id} supplierName={summary.name} graded={lookups.graded} live={live} onClose={() => setSummary(null)} />}
    </div>
  );
}

function SheetTable({ sheets, live, onEdit, onDelete, onSummary, onCount }) {
  return (
    <table className="trans-table">
      <thead>
        <tr>
          <th>Crabber Name</th><th>Date Received</th><th>Date Processed</th><th>Crab Cost</th><th>Transportation Cost</th><th>Total Crabs</th><th>Total Yield</th>
          <th>Total Yield %</th><th>Male Retail Ratio</th><th>Female Retail Ratio</th><th>Total Retail Ratio</th><th className="hide-in-print">Actions</th>
        </tr>
      </thead>
      <tbody>
        {sheets.map((r) => {
          const ratio = listRatios(r);
          return (
            <tr key={`${r.purchaseOrderId}-${r.transportationCost}-${r.igrId}`}>
              <td>{r.crabberName}</td><td>{usDate(r.dateReceived)}</td><td>{usDate(r.dateProcessed)}</td><td>{money(r.totalAmount)}</td><td>{money(r.transportationCost)}</td>
              <td>{r.totalCrabs} {r.unitsOfMeasure === 'LBS' ? 'LBS' : 'BU'}</td><td>{r.totalYield} Bushel(s)</td>
              <td>{ratio.yieldPct}</td><td>{ratio.male}</td><td>{ratio.female}</td><td>{ratio.total}</td>
              <td className="hide-in-print">
                <div className="ys-icons">
                  <button type="button" title="Edit" aria-label="Edit" onClick={() => onEdit(r.purchaseOrderId)}><Pencil size={15} /></button>
                  <button type="button" title={live ? 'Delete' : 'Deleting still happens in the admin portal'} aria-label="Delete" disabled={!live} onClick={() => onDelete(r)}><Trash2 size={15} /></button>
                  <button type="button" title="Summary" aria-label="Summary" onClick={() => onSummary(r)}><BarChart2 size={15} /></button>
                  <button type="button" title="Counting mode" aria-label="Counting mode" disabled={r.statusId === 1} onClick={() => onCount(r)}><Calculator size={15} /></button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}